import type { CheckboxProps } from '@farol-ds/react'
import { BodyText, Button, Checkbox, FormControl, Input, Link } from '@farol-ds/react'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { validateBirthDate, validateCpf } from '@jusbrasil-web/shared-utils-validators'
import { useRouter } from 'next/router'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useIMask } from 'react-imask'

import { externalAuthApi } from '../../api'
import type { AnalyticsEventData } from '../../api/types'
import { DEFAULT_ERROR_MESSAGE, ROUTES, SIGNUP_EVENTS } from '../../constants'
import { useSignupFlowOptional } from '../../contexts'
import {
  type SignupCpfField,
  type SignupCpfFieldErrorReason,
  type SignupCpfFieldsLayout,
  type SignupCpfFieldsVariant,
  resolveSignupCpfFieldsLayout,
  SIGNUP_CPF_FIELDS_EVENTS,
  SIGNUP_CPF_FIELDS_FLAG_KEY,
} from '../../experiments/signup-cpf-fields'
import { useSentryExperimentTag } from '../../hooks'
import { ERROR_MESSAGES } from '../../utils/errorMessages'
import type { AuthPageEventData } from '../../utils/event-data'
import { buildUserAccountEventData } from '../../utils/event-data'
import { isBelowMinimumAge } from '../../utils/is-below-minimum-age'
import { redirectTo } from '../../utils/redirect'
import { clearSignupDataCookie, readSignupDataFromCookie } from '../../utils/signup-data-cookie'
import styles from './signup-cpf-form.module.scss'

const PHONE_DIGITS_VALID_LENGTHS = [10, 11]
const TERMS_OF_USE_URL = 'https://www.jusbrasil.com.br/termos-de-uso'
const PRIVACY_POLICY_URL = 'https://www.jusbrasil.com.br/politica-de-privacidade'

// Same wording already in production on the RadarCPF surfaces (see
// complete-signup-nudge/complete-signup-modal/cpf-dob-step.tsx).
const CPF_DESCRIPTION = 'Para consultar seus processos'
const BIRTH_DATE_DESCRIPTION = 'Ajuda a confirmar que a consulta é sobre você'

// Standalone renders (and any caller outside the experiment) keep production.
const CONTROL_LAYOUT = resolveSignupCpfFieldsLayout('control')

function getCpfErrorMessage(unmasked: string): string {
  const digits = (unmasked || '').replace(/\D/g, '')
  return digits.length === 0
    ? ERROR_MESSAGES.signup.cpf.cpf.empty
    : ERROR_MESSAGES.signup.cpf.cpf.invalid
}

function getCpfErrorReason(unmasked: string): SignupCpfFieldErrorReason {
  const digits = (unmasked || '').replace(/\D/g, '')
  if (digits.length === 0) return 'empty'
  // A partially typed CPF fails validateCpf for a different reason than a
  // complete-but-wrong one — worth separating in the guardrail.
  if (digits.length < 11) return 'incomplete'
  return 'invalid'
}

function getBirthDateErrorReason(masked: string): SignupCpfFieldErrorReason {
  return masked ? 'invalid' : 'empty'
}

// Exported: `EntityNoticeEntryEmailFirst`'s own optional WhatsApp field uses
// the identical digit-stripping + valid-lengths check (10 or 11 digits, empty
// counts as not-yet-filled) rather than re-deriving it.
export function getPhoneDigits(value: string): string {
  return (value || '').replace(/\D/g, '')
}

export function isPhoneValid(phoneDigits: string): boolean {
  if (phoneDigits.length === 0) return true
  return PHONE_DIGITS_VALID_LENGTHS.includes(phoneDigits.length)
}

export interface SignupCpfFormExperiment {
  variant: SignupCpfFieldsVariant | null
  inExperiment: boolean
}

export interface SignupCpfFormProps {
  signUpByEmailToken: string | null
  email?: string | null
  nextUrl?: string | null
  onSubmit?: (data: {
    cpf: string
    birthdate: string
    phone?: string
    whatsAppOptIn?: boolean
  }) => void
  onError?: (error: string | undefined) => void
  onBelowMinimumAgeError?: (isBelowMinAge: boolean) => void
  isGoogleFlow?: boolean
  requiresPhoneStep?: boolean
  /**
   * Hides the "Telefone (opcional)" field and the WhatsApp opt-in checkbox
   * without implying a follow-up `/cadastro/telefone` redirect the way
   * `requiresPhoneStep` does. The entity-notice fake-door's Finalize step
   * uses this: Figma has no phone field on that screen for any of its three
   * variants — cell A's phone travels through
   * `entity-notice-phone-storage.ts` instead, read back by
   * `useEntityNoticeFinalizeSignup`; B/C never collect one at all. The
   * `onSubmit` escape hatch still receives `phone`/`whatsAppOptIn` from this
   * form (always `undefined`/`false` while hidden) — callers relying on the
   * stored fallback merge it in afterwards, this prop only controls the
   * visible UI.
   */
  hidePhoneFields?: boolean
  eventData?: AuthPageEventData
  experiment?: SignupCpfFormExperiment
  layout?: SignupCpfFieldsLayout
}

export function SignupCpfForm({
  signUpByEmailToken,
  email: emailProp,
  nextUrl,
  onSubmit,
  onError,
  onBelowMinimumAgeError,
  isGoogleFlow = false,
  requiresPhoneStep = false,
  hidePhoneFields = false,
  eventData,
  experiment,
  layout = CONTROL_LAYOUT,
}: SignupCpfFormProps) {
  const router = useRouter()
  const signupFlowContext = useSignupFlowOptional()
  const [isLoading, setIsLoading] = useState(false)
  const [birthDateError, setBirthDateError] = useState(false)
  const [cpfError, setCpfError] = useState(false)
  const [phoneError, setPhoneError] = useState(false)
  const [birthDateTouched, setBirthDateTouched] = useState(false)
  const [cpfTouched, setCpfTouched] = useState(false)
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [whatsAppOptIn, setWhatsAppOptIn] = useState(false)
  const [belowMinimumAgeBlocked, setBelowMinimumAgeBlocked] = useState(false)
  const belowMinimumAgeEventFiredRef = useRef(false)
  // Last reason already reported per field, so holding a bad CPF through several
  // blurs and a submit sends one event, not one per interaction.
  const firedFieldErrorsRef = useRef(new Map<SignupCpfField, SignupCpfFieldErrorReason>())

  useSentryExperimentTag(SIGNUP_CPF_FIELDS_FLAG_KEY, experiment?.variant)

  // Argos persists only the columns that exist in `useraccount_events`; any
  // other top-level property is dropped on the way in (measured: the events
  // landed with every column null). `metadata` is the free-form JSON column,
  // so every experiment dimension has to travel inside it.
  const experimentMetadata = useMemo(() => {
    const meta: Record<string, string> = {
      flow: isGoogleFlow ? 'google' : 'email',
      in_experiment: String(experiment?.inExperiment ?? false),
      requires_phone_step: String(requiresPhoneStep),
    }
    if (experiment?.variant) meta.variant = experiment.variant
    return meta
  }, [experiment?.variant, experiment?.inExperiment, isGoogleFlow, requiresPhoneStep])

  const experimentEventData = useMemo(
    () => ({ ...buildUserAccountEventData({}), metadata: experimentMetadata }),
    [experimentMetadata]
  )

  const fireFieldValidationError = useCallback(
    (field: SignupCpfField, reason: SignupCpfFieldErrorReason) => {
      if (firedFieldErrorsRef.current.get(field) === reason) return
      firedFieldErrorsRef.current.set(field, reason)
      sendMetrics(SIGNUP_CPF_FIELDS_EVENTS.FIELD_VALIDATION_ERROR, null, {
        ...experimentEventData,
        metadata: { ...experimentMetadata, field, reason },
      })
    },
    [experimentEventData, experimentMetadata]
  )

  const clearFieldValidationError = useCallback((field: SignupCpfField) => {
    firedFieldErrorsRef.current.delete(field)
  }, [])

  const {
    ref: cpfRef,
    unmaskedValue: cpfUnmasked,
    value: cpfValue,
  } = useIMask(useRef({ mask: '000.000.000-00', lazy: false }).current)
  const { ref: birthDateRef, value: birthDateValue } = useIMask(
    useRef({ mask: '00/00/0000', lazy: false }).current
  )
  const {
    ref: phoneRef,
    unmaskedValue: phoneUnmasked,
    value: phoneValue,
  } = useIMask(
    useRef({
      mask: '(00) 00000-0000',
      lazy: false,
      placeholderChar: '_',
    }).current
  )

  const getBirthDateIso = useCallback(() => {
    if (!birthDateValue) return ''
    return birthDateValue.split('/').reverse().join('-')
  }, [birthDateValue])

  const handleBirthDateBlur = useCallback(() => {
    setBirthDateTouched(true)
    const iso = getBirthDateIso()
    const valid = validateBirthDate(iso)
    setBirthDateError(!valid)

    if (valid) {
      clearFieldValidationError('birthdate')
    } else {
      fireFieldValidationError('birthdate', getBirthDateErrorReason(birthDateValue || ''))
    }

    const blocked = valid && isBelowMinimumAge(iso)
    setBelowMinimumAgeBlocked(blocked)
    onBelowMinimumAgeError?.(blocked)

    if (blocked && !belowMinimumAgeEventFiredRef.current) {
      belowMinimumAgeEventFiredRef.current = true
      sendMetrics(SIGNUP_EVENTS.BELOW_MINIMUM_AGE_BLOCKED, null, experimentEventData)
    }
    if (!blocked) {
      belowMinimumAgeEventFiredRef.current = false
    }
  }, [
    getBirthDateIso,
    onBelowMinimumAgeError,
    birthDateValue,
    experimentEventData,
    fireFieldValidationError,
    clearFieldValidationError,
  ])

  const handleCpfBlur = useCallback(() => {
    setCpfTouched(true)
    const cleaned = (cpfUnmasked || '').replace(/\D/g, '')
    const invalid = cleaned.length === 11 && !validateCpf(cleaned)
    setCpfError(invalid)
    if (invalid) {
      fireFieldValidationError('cpf', 'invalid')
    } else {
      clearFieldValidationError('cpf')
    }
  }, [cpfUnmasked, fireFieldValidationError, clearFieldValidationError])

  const handlePhoneBlur = useCallback(() => {
    setPhoneTouched(true)
    const digits = getPhoneDigits(phoneUnmasked || '')
    const invalid = digits.length > 0 && !isPhoneValid(digits)
    setPhoneError(invalid)
    if (invalid) {
      fireFieldValidationError('phone', 'incomplete')
    } else {
      clearFieldValidationError('phone')
    }
  }, [phoneUnmasked, fireFieldValidationError, clearFieldValidationError])

  const handleWhatsAppOptInChange = useCallback(
    (checked: Parameters<NonNullable<CheckboxProps['onCheckedChange']>>[0]) => {
      setWhatsAppOptIn(checked === true)
      if (checked === true) {
        const digits = getPhoneDigits(phoneUnmasked || '')
        const valid = digits.length > 0 && isPhoneValid(digits)
        if (!valid) {
          setPhoneTouched(true)
          setPhoneError(true)
          fireFieldValidationError('phone', 'required_for_optin')
        } else {
          setPhoneError(false)
          clearFieldValidationError('phone')
        }
      } else {
        setPhoneTouched(false)
        setPhoneError(false)
        clearFieldValidationError('phone')
      }
    },
    [phoneUnmasked, fireFieldValidationError, clearFieldValidationError]
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      const cpf = cpfUnmasked || ''
      const birthDateIso = getBirthDateIso()
      const isBirthDateValid = validateBirthDate(birthDateIso)
      const cleanedCpf = cpf.replace(/\D/g, '')
      const isCpfValid = cleanedCpf.length === 11 && validateCpf(cleanedCpf)

      const isBelowMinAge = isBirthDateValid && isBelowMinimumAge(birthDateIso)
      setBelowMinimumAgeBlocked(isBelowMinAge)
      onBelowMinimumAgeError?.(isBelowMinAge)

      if (isBelowMinAge && !belowMinimumAgeEventFiredRef.current) {
        belowMinimumAgeEventFiredRef.current = true
        sendMetrics(SIGNUP_EVENTS.BELOW_MINIMUM_AGE_BLOCKED, null, experimentEventData)
      } else if (!isBelowMinAge) {
        belowMinimumAgeEventFiredRef.current = false
      }

      const phoneDigits = getPhoneDigits(phoneUnmasked || '')
      const isPhoneValidValue = isPhoneValid(phoneDigits)
      const hasPhoneValue = phoneDigits.length > 0

      const phoneValidForOptIn = hasPhoneValue && isPhoneValidValue
      const phoneErrorIfOptIn = whatsAppOptIn && !phoneValidForOptIn
      const isPhoneInvalid = (hasPhoneValue && !isPhoneValidValue) || phoneErrorIfOptIn

      setBirthDateTouched(true)
      setCpfTouched(true)
      setPhoneTouched(true)
      setBirthDateError(!isBirthDateValid)
      setCpfError(!isCpfValid)
      setPhoneError(isPhoneInvalid)

      if (isCpfValid) {
        clearFieldValidationError('cpf')
      } else {
        fireFieldValidationError('cpf', getCpfErrorReason(cpf))
      }
      if (isBirthDateValid) {
        clearFieldValidationError('birthdate')
      } else {
        fireFieldValidationError('birthdate', getBirthDateErrorReason(birthDateValue || ''))
      }
      if (hasPhoneValue && !isPhoneValidValue) {
        fireFieldValidationError('phone', 'incomplete')
      } else if (phoneErrorIfOptIn) {
        fireFieldValidationError('phone', 'required_for_optin')
      } else {
        clearFieldValidationError('phone')
      }

      if (isBelowMinAge) {
        return
      }

      const validPhone = hasPhoneValue && isPhoneValidValue ? phoneDigits : undefined

      // The phone-validity check above must run before this escape hatch: the
      // server accepts 10/11-digit phones silently and would otherwise never
      // surface `phoneErrorIfOptIn`/an incomplete phone to the caller.
      if (isPhoneInvalid) {
        return
      }

      if (onSubmit) {
        // Only this step's own checkbox counts as an explicit opt-in signal
        // when it also collected a phone: without an own phone the checkbox
        // is just sitting at its unchanged default, and `undefined` here lets
        // the caller (e.g. `useEntityNoticeFinalizeSignup`) fall back to a
        // consent captured elsewhere instead of silently overriding it with
        // `false`.
        const submittedWhatsAppOptIn = validPhone !== undefined ? whatsAppOptIn : undefined
        onSubmit({
          cpf,
          birthdate: birthDateIso,
          whatsAppOptIn: submittedWhatsAppOptIn,
          phone: validPhone,
        })
        return
      }

      if (!signUpByEmailToken && !isGoogleFlow) {
        onError?.(ERROR_MESSAGES.signup.cpf.tokenNotProvided)
        return
      }

      if (!isBirthDateValid || !isCpfValid) {
        return
      }

      const birthDateTimestamp = new Date(`${birthDateIso}T00:00:00-03:00`).getTime()

      const signupData = readSignupDataFromCookie()

      setIsLoading(true)
      onError?.(undefined)

      const analyticsEventData: AnalyticsEventData = {}
      if (eventData?.appId !== undefined) analyticsEventData.app_id = eventData.appId
      if (eventData?.featureCode !== undefined)
        analyticsEventData.feature_code = eventData.featureCode
      if (eventData?.fromComponent !== undefined)
        analyticsEventData.from_component = eventData.fromComponent
      const hasAnalyticsEventData = Object.keys(analyticsEventData).length > 0
      if (hasAnalyticsEventData) analyticsEventData.type = 'SignUpModal'

      try {
        const { response, data: authData } = await externalAuthApi.completeSignupWithCpf({
          ...(signUpByEmailToken && { token: signUpByEmailToken }),
          cpf: cleanedCpf,
          birthDate: birthDateTimestamp,
          ...(validPhone !== undefined && { phone: validPhone }),
          whatsAppOptIn,
          ...(hasAnalyticsEventData && { event_data: analyticsEventData }),
        })

        if (!response.ok) {
          const errorMessage = authData?.message || authData?.reason || DEFAULT_ERROR_MESSAGE
          const errorReason = authData?.reason

          if (errorReason === 'first_step_validation' && signUpByEmailToken) {
            const query = nextUrl ? `?next_url=${encodeURIComponent(nextUrl)}` : ''
            redirectTo(`${ROUTES.SIGN_UP.DETAILS}/${signUpByEmailToken}${query}`)
            return
          }

          if (signUpByEmailToken && !isGoogleFlow) {
            if (signupFlowContext) {
              signupFlowContext.setCpfErrorSession({
                email: emailProp ?? null,
                cpf: cleanedCpf,
                birthDateIso,
              })
            }
            const query = nextUrl ? `?next_url=${encodeURIComponent(nextUrl)}` : ''
            router.push(`${ROUTES.SIGN_UP.CPF_NOT_VERIFIED}${query}`)
            return
          }

          onError?.(errorMessage)
          setIsLoading(false)
          return
        }

        if (signupData) {
          clearSignupDataCookie()
        }

        if (requiresPhoneStep) {
          const postStepDestination = authData?.mustRedirectTo || nextUrl
          const query = postStepDestination
            ? `?next_url=${encodeURIComponent(postStepDestination)}`
            : ''
          redirectTo(`${ROUTES.SIGN_UP.PHONE_STEP}${query}`)
          return
        }

        if (authData?.mustRedirectTo) {
          redirectTo(authData.mustRedirectTo)
          return
        }

        const redirectUrl = nextUrl || '/'
        redirectTo(redirectUrl)
      } catch (err) {
        onError?.(err instanceof Error ? err.message : DEFAULT_ERROR_MESSAGE)
        setIsLoading(false)
      }
    },
    [
      cpfUnmasked,
      getBirthDateIso,
      phoneUnmasked,
      whatsAppOptIn,
      signUpByEmailToken,
      emailProp,
      nextUrl,
      router,
      signupFlowContext,
      onSubmit,
      onError,
      onBelowMinimumAgeError,
      isGoogleFlow,
      requiresPhoneStep,
      eventData,
      birthDateValue,
      experimentEventData,
      fireFieldValidationError,
      clearFieldValidationError,
    ]
  )

  const cpfField = (
    <>
      <FormControl error={cpfTouched && cpfError}>
        <FormControl.Label>CPF</FormControl.Label>
        {layout.showFieldDescriptions && (
          <FormControl.Description>{CPF_DESCRIPTION}</FormControl.Description>
        )}
        <Input
          ref={cpfRef}
          type="text"
          name="cpf"
          value={cpfValue || ''}
          inputMode="numeric"
          disabled={isLoading}
          onBlur={handleCpfBlur}
        />
        <FormControl.ErrorMessage>
          {cpfTouched && cpfError && getCpfErrorMessage(cpfUnmasked || '')}
        </FormControl.ErrorMessage>
      </FormControl>
    </>
  )

  const birthDateField = (
    <>
      <FormControl error={birthDateTouched && birthDateError}>
        <FormControl.Label>Data de nascimento</FormControl.Label>
        {layout.showFieldDescriptions && (
          <FormControl.Description>{BIRTH_DATE_DESCRIPTION}</FormControl.Description>
        )}
        <Input
          ref={birthDateRef}
          type="text"
          name="birthdate"
          value={birthDateValue || ''}
          inputMode="numeric"
          disabled={isLoading}
          onBlur={handleBirthDateBlur}
        />
        <FormControl.ErrorMessage>
          {birthDateTouched &&
            birthDateError &&
            (birthDateValue
              ? ERROR_MESSAGES.signup.cpf.birthDate.invalid
              : ERROR_MESSAGES.signup.cpf.birthDate.empty)}
        </FormControl.ErrorMessage>
      </FormControl>
    </>
  )

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {/* Static fragments, not an array + keys: the masks are bound by ref to
          specific inputs, and remounting an Input would drop its useIMask state. */}
      {layout.fieldOrder === 'birthdate_first' ? (
        <>
          {birthDateField}
          {cpfField}
        </>
      ) : (
        <>
          {cpfField}
          {birthDateField}
        </>
      )}
      {!requiresPhoneStep && !hidePhoneFields && (
        <FormControl error={phoneTouched && phoneError}>
          <FormControl.Label>Telefone (opcional)</FormControl.Label>
          <Input
            ref={phoneRef}
            type="text"
            name="phone"
            value={phoneValue || ''}
            inputMode="tel"
            disabled={isLoading}
            onBlur={handlePhoneBlur}
          />
          <FormControl.ErrorMessage>
            {phoneTouched && phoneError && ERROR_MESSAGES.signup.cpf.phone.invalid}
          </FormControl.ErrorMessage>
        </FormControl>
      )}
      {!requiresPhoneStep && !hidePhoneFields && (
        <Checkbox.Item>
          <Checkbox
            checked={whatsAppOptIn}
            onCheckedChange={handleWhatsAppOptInChange}
            disabled={isLoading}
          />
          <Checkbox.Content>
            <Checkbox.Name>Aceito receber notificações no WhatsApp</Checkbox.Name>
          </Checkbox.Content>
        </Checkbox.Item>
      )}
      <BodyText className={styles.agreementText}>
        Concordo com os{' '}
        <Link className={styles.agreementLink} href={TERMS_OF_USE_URL}>
          Termos de Uso
        </Link>{' '}
        e a{' '}
        <Link className={styles.agreementLink} href={PRIVACY_POLICY_URL}>
          Política de Privacidade
        </Link>
        .
      </BodyText>
      <Button type="submit" kind="primary" disabled={isLoading || belowMinimumAgeBlocked}>
        {isLoading ? 'Criando conta...' : 'Criar conta'}
      </Button>
    </form>
  )
}
